import React, { FC, useCallback, useMemo } from 'react';

import { ICombo, IComboSlotOption } from '@rbi-ctg/menu';
import { ListCardGroup } from 'components/list-card-group';
import { NUM_COLUMNS } from 'components/list-card-group/styled';
import { ProductCard } from 'components/product-card';
import { MenuObjectTypes } from 'enums/menu';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { isItem, isPicker } from 'utils/menu';

import { transformItemToProductListItemProps } from '../utils';

import { ProductSelectedItemCustomization } from './product-selected-item-customization';
import { IProductCustomizationOptionsProps } from './types';
import { getRowIndex, getSelectedRowIndex } from './utils';

type ComboSlotOptionWithPrice = IComboSlotOption & { price?: number };

export const ProductCustomizationOptions: FC<IProductCustomizationOptionsProps> = ({
  slotKeyToCustomize,
  comboSlot,
  comboSlotOptions,
  selectedItem,
  onSelectComboSlot,
  comboSlotType,
  modifierSelections,
  modifierDefaults,
  onModifierSelectionsChange,
  pickerAspectExclusions,
}) => {
  const { priceForComboSlotSelection } = useMenuContext();
  const {
    selectedProduct,
    comboSlotUpdate,
    menuObject,
    updateSelectionStateWithId,
  } = useProductWizardContext();
  const { isFromCart } = useProductWizardFlow();
  const { formatCalories } = useMenuCalories();

  const isMenuObjectPicker = isPicker(menuObject);

  const [selectedComboSlotRow, setSelectedComboSlotRow] = React.useState<{
    comboSlotOption: ComboSlotOptionWithPrice;
    rowIndex: number;
  } | null>(null);

  React.useEffect(() => {
    if (comboSlotOptions.length > 0 && selectedItem) {
      const selectedRowIndex = getSelectedRowIndex({ selectedItem, comboSlotOptions });
      const selectedComboSlotOption =
        selectedRowIndex === -1 ? comboSlotOptions[0] : comboSlotOptions[selectedRowIndex];

      setSelectedComboSlotRow({
        comboSlotOption: selectedComboSlotOption,
        rowIndex: getRowIndex(selectedRowIndex, NUM_COLUMNS),
      });
    }
  }, [selectedItem, comboSlotOptions]);

  const comboSlotOptionsWithPrice = useMemo<ComboSlotOptionWithPrice[]>(() => {
    return comboSlotOptions.map(option => {
      const upsellPrice = comboSlot
        ? priceForComboSlotSelection({
            combo: selectedProduct as ICombo,
            comboSlot,
            selectedOption: option,
          })
        : 0;
      return {
        ...option,
        price: upsellPrice,
      };
    });
  }, [comboSlot, comboSlotOptions, priceForComboSlotSelection, selectedProduct]);

  const handleSelectComboSlot = useCallback(
    (comboSlotOption: IComboSlotOption, rowIndex: number) => {
      const item = comboSlotOption.option;

      if (!isItem(item)) {
        return;
      }

      // Update selectedComboSlotRow if there are options
      if (item.options.length) {
        setSelectedComboSlotRow({ comboSlotOption, rowIndex });
      } else {
        setSelectedComboSlotRow(null);
      }

      onSelectComboSlot(comboSlotOption);
    },
    [onSelectComboSlot]
  );

  const selectedItemCustomization = React.useMemo(() => {
    const hasAvailableOptions = !!selectedComboSlotRow?.comboSlotOption.option.options.length;

    const shouldDisplayItemCustomization =
      comboSlotType && selectedComboSlotRow && hasAvailableOptions;

    return shouldDisplayItemCustomization ? (
      <ProductSelectedItemCustomization
        selectedItem={selectedItem}
        modifierDefaults={modifierDefaults ?? {}}
        modifierSelections={modifierSelections ?? {}}
        onModifierSelectionsChange={onModifierSelectionsChange!}
        onComboSlotModifierSelectionsUpdate={() => {
          updateSelectionStateWithId({
            id: slotKeyToCustomize,
            isDirty: true,
            type: MenuObjectTypes.COMBO_SLOT,
          });
        }}
        onResetModifierSelections={() => undefined}
        pickerAspectExclusions={pickerAspectExclusions}
      />
    ) : null;
  }, [
    comboSlotType,
    modifierDefaults,
    modifierSelections,
    onModifierSelectionsChange,
    pickerAspectExclusions,
    selectedComboSlotRow,
    selectedItem,
    slotKeyToCustomize,
    updateSelectionStateWithId,
  ]);

  const foundComboSlotCustomizations = comboSlotUpdate.find(comboSlotUpdateVal => {
    return slotKeyToCustomize === comboSlotUpdateVal.id && !!comboSlotUpdateVal.isDirty;
  });

  return (
    <ListCardGroup
      selectedRow={selectedComboSlotRow?.rowIndex}
      numColumns={NUM_COLUMNS}
      items={comboSlotOptionsWithPrice}
      ItemCustomizationComponent={
        isFromCart || !isPicker(menuObject) || !!foundComboSlotCustomizations
          ? selectedItemCustomization
          : null
      }
      renderItem={({ item: comboSlotOption, rowIndex }) => {
        const productListItemProps = transformItemToProductListItemProps(comboSlotOption.option);

        const isSelected =
          selectedItem._id === comboSlotOption.option._id &&
          (isFromCart || !isMenuObjectPicker || !!foundComboSlotCustomizations);

        return (
          <ProductCard
            key={comboSlotOption.option._id}
            isSelected={isSelected}
            onProductPress={() => handleSelectComboSlot(comboSlotOption, rowIndex)}
            accessibilityRole="radio"
            accessibilitySelected={isSelected}
            productProps={{
              ...productListItemProps.productProps,
              price: comboSlotOption.price,
              calories: formatCalories(comboSlotOption.option.nutrition?.calories),
            }}
          />
        );
      }}
    />
  );
};
